import { ShieldCheck, Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Globe, Users, Info, Coins } from "lucide-react";
import OriginalBadge from "@/components/pro/OriginalBadge";

export default function PostCard({ post, mode }) {
  const isHorizonte = mode === "horizonte";

  return (
    <article
      data-testid={`post-${post.id}`}
      className="border hairline bg-cream-card rounded-2xl mb-5 overflow-hidden"
    >
      {/* Header */}
      <header className="flex items-start gap-3 p-4">
        <img src={post.author.avatar} alt="" className="w-11 h-11 rounded-full ring-2 ring-cream-deep" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-ink">{post.author.name}</span>
            {post.author.verified && <ShieldCheck className="w-3.5 h-3.5 text-coral" />}
            <span className="text-xs text-ink-mid">·</span>
            <span className="text-xs text-ink-mid">{post.author.handle}</span>
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-cream-deep/60 text-ink-mid font-mono uppercase tracking-wider text-[10px]">
              {post.author.badge}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-ink-mid">
            <span>{post.time}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              {isHorizonte ? <Globe className="w-3 h-3" /> : <Users className="w-3 h-3" />}
              {post.audience}
            </span>
          </div>
        </div>
        <button data-testid={`post-${post.id}-more`} className="text-ink-mid hover:text-ink transition p-1">
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </header>

      {/* Recommendation reason — only for Horizonte */}
      {isHorizonte && post.reason && (
        <div className="mx-4 mb-3 px-3 py-2 bg-coral/5 border border-coral/15 rounded-xl flex items-start gap-2 text-xs text-ink/75">
          <Info className="w-3.5 h-3.5 text-coral mt-0.5 shrink-0" />
          <span>
            <span className="font-mono uppercase tracking-[0.18em] text-[10px] text-coral mr-1.5">¿Por qué esto?</span>
            {post.reason}.{" "}
            <button className="underline hover:text-coral">No mostrarme más</button>
          </span>
        </div>
      )}

      {/* Body text */}
      <div className="px-4 pb-3">
        <p className="text-[15px] text-ink/90 leading-relaxed text-pretty">{post.text}</p>
      </div>

      {/* Image */}
      {post.image && (
        <div className="img-reveal max-h-[560px] overflow-hidden bg-cream-deep relative">
          <img src={post.image} alt="" className="w-full h-full object-cover" loading="lazy" />
          {post.signature && (
            <div data-testid={`post-${post.id}-original`} className="absolute top-3 left-3">
              <OriginalBadge size="lg" />
            </div>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="px-4 pt-3 flex items-center justify-between text-xs text-ink-mid">
        <div className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-coral text-cream">
            <Heart className="w-3 h-3" fill="currentColor" />
          </span>
          <span>{post.stats.likes.toLocaleString("es")}</span>
        </div>
        <div className="flex items-center gap-3">
          <span>{post.stats.comments} comentarios</span>
          <span>{post.stats.shares} compartidos</span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-2 py-1 mt-2 mx-2 mb-3 border-t hairline flex items-center justify-around">
        <Action icon={Heart}         label="Me gusta"   testid={`post-${post.id}-like`} />
        <Action icon={MessageCircle} label="Comentar"   testid={`post-${post.id}-comment`} />
        <Action icon={Share2}        label="Compartir"  testid={`post-${post.id}-share`} />
        <Action icon={Coins}         label="Apoyar"     testid={`post-${post.id}-tip`} accent />
        <Action icon={Bookmark}      label="Guardar"    testid={`post-${post.id}-save`} />
      </div>
    </article>
  );
}

function Action({ icon: Icon, label, testid, accent = false }) {
  return (
    <button
      data-testid={testid}
      className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 mt-1 text-sm rounded-xl transition-colors ${
        accent
          ? "text-coral hover:bg-coral/10"
          : "text-ink/75 hover:bg-cream-deep/50 hover:text-ink"
      }`}
    >
      <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
